import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardBody, Col, Row } from "reactstrap";
import { ticketsManagement } from "../../helpers/backend_helper";
import { toast } from "react-toastify";

export default function ViewPublicTicket() {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [incidentTypeName, setIncidentTypeName] = useState("");

  const navigate = useNavigate();
  const { serialId, caller } = useParams();

  useEffect(() => {
    if (serialId) {
      fetchTicket();
    }
  }, [serialId]);

  useEffect(() => {
    if (data.callerPhone && caller && caller !== data.callerPhone) {
      navigate("/not-authorized");
      return;
    }
    
    if (data.incidentType) {
      const incidents = JSON.parse(localStorage.getItem("incidentTypes") || "[]");
      const matchedIncident = incidents.find(
        incident => incident.incidentTypeId === data.incidentType
      );
      if (matchedIncident) {
        setIncidentTypeName(matchedIncident.incidentName);
      }
    }
  }, [data, caller, navigate]);

  const fetchTicket = async () => {
    setIsLoading(true);
    const payload = {
      act: "ticket",
      subact: "getBySerialId",
      info: {
        serialId: serialId,
      },
    };
    
    try {
      const response = await ticketsManagement(payload);
      if (response?.data) {
        setData(response.data);
      } else {
        toast.error("Failed to fetch ticket details", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch(error) {
      console.error("Error fetching ticket:", error);
      toast.error("Error fetching ticket details", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'assigned':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'pending':
        return 'danger';
      case 'closed':
        return 'dark';
      default:
        return 'info';
    }
  };

  return (
    <div className="ticket-view-container">
      <Card className="shadow-sm">
        <CardBody>
          {/* Header Section */}
          <div className="ticket-header p-2 mb-4">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
              <div>
                <h4 className="mb-1">
                  <i className="bx bx-file me-2"></i>
                  Ticket: {data.serialId}
                </h4>
                <p className="text-muted small mb-0">Created: {formatDate(data.createdAt)}</p>
              </div>
              <div className="mt-2 mt-md-0">
                <span className={`badge bg-${getStatusBadgeColor(data.status)} px-3 py-2`}>
                  <i className="bx bx-check-circle me-1"></i>
                  {data.status}
                </span>
              </div>
            </div>
          </div>

          {/* Basic Information Section */}
          <div className="info-section mb-4">
            <div className="section-title d-flex align-items-center mb-3">
              <i className="bx bx-info-circle fs-5 me-2 text-primary"></i>
              <h5 className="m-0">Basic Information</h5>
            </div>
            <div className="section-content p-3 bg-light rounded">
              <Row>
                <Col xs={12} md={6} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Incident Type</span>
                    <p className="info-value fw-medium mb-0">{incidentTypeName || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Address</span>
                    <p className="info-value fw-medium mb-0">{data.address || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Caller</span>
                    <p className="info-value fw-medium mb-0">{data.callerName || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={12} md={6} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Contact</span>
                    <p className="info-value fw-medium mb-0">{data.callerPhone || "N/A"}</p>
                  </div>
                </Col>
              </Row>
            </div>
          </div>

          {/* Location Section */}
          <div className="location-section mb-4">
            <div className="section-title d-flex align-items-center mb-3">
              <i className="bx bx-map fs-5 me-2 text-success"></i>
              <h5 className="m-0">Location Information</h5>
            </div>
            <div className="section-content p-3 bg-light rounded">
              <Row>
                <Col xs={6} sm={6} md={3} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Division</span>
                    <p className="info-value fw-medium mb-0">{data.divisionName || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={6} sm={6} md={3} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Range</span>
                    <p className="info-value fw-medium mb-0">{data.rangeName || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={6} sm={6} md={3} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Round</span>
                    <p className="info-value fw-medium mb-0">{data.roundName || "N/A"}</p>
                  </div>
                </Col>
                <Col xs={6} sm={6} md={3} className="mb-3">
                  <div className="info-item">
                    <span className="info-label text-muted">Beat</span>
                    <p className="info-value fw-medium mb-0">{data.beatName || "N/A"}</p>
                  </div>
                </Col>
              </Row>
              
              {data.incidentLocation && (
                <div className="mt-2">
                  <span className="info-label text-muted d-block mb-1">GPS Coordinates</span>
                  <div className="d-flex align-items-center">
                    <i className="bx bx-current-location text-danger me-2"></i>
                    <p className="info-value mb-0">
                      {data.incidentLocation.lat.toFixed(6)}, {data.incidentLocation.long.toFixed(6)}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Incident Details Section */}
          <div className="incident-section mb-4">
            <div className="section-title d-flex align-items-center mb-3">
              <i className="bx bx-detail fs-5 me-2 text-danger"></i>
              <h5 className="m-0">Incident Details</h5>
            </div>
            <div className="section-content p-3 bg-light rounded">
              <Row className="mb-3">
                <Col xs={12} md={6} className="mb-3 mb-md-0">
                  <div className="info-item">
                    <span className="info-label text-muted">Incident Date</span>
                    <p className="info-value fw-medium mb-0">{formatDate(data.incidentDate)}</p>
                  </div>
                </Col>
                <Col xs={12} md={6}>
                  <div className="info-item">
                    <span className="info-label text-muted">Incident Time</span>
                    <p className="info-value fw-medium mb-0">{formatDate(data.incidentTime)}</p>
                  </div>
                </Col>
              </Row>
              
              <div className="mt-3">
                <span className="info-label text-muted">Description</span>
                <div className="description-box p-3 mt-1 border rounded bg-white">
                  {data.description || "No description provided."}
                </div>
              </div>
            </div>
          </div>

          {/* Media Section */}
          {data.media && data.media.length > 0 && (
            <div className="media-section mb-4">
              <div className="section-title d-flex align-items-center mb-3">
                <i className="bx bx-images fs-5 me-2 text-info"></i>
                <h5 className="m-0">Media Files</h5>
              </div>
              <div className="section-content p-3 bg-light rounded">
                <div className="media-gallery d-flex flex-wrap">
                  {data.media.map((mediaItem, index) => (
                    <div key={index} className="me-2 mb-2">
                      <a 
                        href={mediaItem} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="btn btn-sm btn-outline-primary"
                      >
                        <i className="bx bx-file me-1"></i>
                        Media {index + 1}
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Assigned Users Section */}
          <div className="assigned-users-section mb-4">
            <div className="section-title d-flex align-items-center mb-3">
              <i className="bx bx-user-check fs-5 me-2 text-warning"></i>
              <h5 className="m-0">Assigned Personnel</h5>
            </div>
            
            {/* Range Officers Table */}
            {data.assignedRangeUser3 && data.assignedRangeUser3.length > 0 ? (
              <div className="user-table mb-4">
                <h6 className="mb-2 bg-light p-2 rounded">
                  <i className="bx bx-shield-quarter me-1"></i>
                  Range Officers
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-striped table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.assignedRangeUser3.map((user, index) => (
                        <tr key={index}>
                          <td>{user.lastName || "N/A"}</td>
                          <td>
                            {user.contactNumber ? (
                              <a href={`tel:${user.contactNumber}`}>
                                <i className="bx bx-phone me-1"></i>
                                {user.contactNumber}
                              </a>
                            ) : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bx bx-info-circle me-2"></i>
                No range officers assigned.
              </div>
            )}
            
            {/* Round Officers Table */}
            {data.assignedRoundUser3 && data.assignedRoundUser3.length > 0 ? (
              <div className="user-table">
                <h6 className="mb-2 bg-light p-2 rounded">
                  <i className="bx bx-trip me-1"></i>
                  Round Officers
                </h6>
                <div className="table-responsive">
                  <table className="table table-sm table-striped table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>Name</th>
                        <th>Round</th>
                        <th>Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.assignedRoundUser3.map((user, index) => (
                        <tr key={index}>
                          <td>{user.lastName || "N/A"}</td>
                          <td>{user.roundName || "N/A"}</td>
                          <td>
                            {user.contactNumber ? (
                              <a href={`tel:${user.contactNumber}`}>
                                <i className="bx bx-phone me-1"></i>
                                {user.contactNumber}
                              </a>
                            ) : "N/A"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="alert alert-info">
                <i className="bx bx-info-circle me-2"></i>
                No round officers assigned.
              </div>
            )}
          </div>
          
          {/* Back button at the bottom */}
          {/* <div className="d-flex justify-content-center mt-4">
            <button 
              className="btn btn-outline-secondary px-4"
              onClick={() => window.history.back()}
            >
              <i className="bx bx-arrow-back me-1"></i>
              Back
            </button>
          </div> */}
        </CardBody>
      </Card>
      
      {/* Custom CSS for mobile optimization */}
      <style jsx>{`
        /* Global styles for the ticket view */
        .ticket-view-container {
          max-width: 100%;
          margin: 0 auto;
        }
        
        .section-title {
          border-bottom: 1px solid #eee;
          padding-bottom: 8px;
        }
        
        .info-label {
          display: block;
          font-size: 0.75rem;
          margin-bottom: 2px;
        }
        
        .info-value {
          font-size: 0.95rem;
        }
        
        .description-box {
          min-height: 60px;
          white-space: pre-wrap;
        }
        
        /* Responsive table styles */
        @media (max-width: 767px) {
          .table-responsive {
            border: 0;
          }
          
          .table-responsive table {
            font-size: 0.85rem;
          }
          
          .ticket-header h4 {
            font-size: 1.2rem;
          }
          
          .section-title h5 {
            font-size: 1rem;
          }
        }
        
        /* Cards and sections */
        .card {
          border-radius: 8px;
          overflow: hidden;
          border: none;
        }
        
        .section-content {
          border-radius: 6px;
        }
        
        /* Making elements more touch-friendly */
        @media (max-width: 767px) {
          .btn {
            padding: 0.375rem 0.75rem;
            min-height: 40px;
          }
          
          .info-item {
            margin-bottom: 0.5rem;
          }
        }
      `}</style>
    </div>
  );
}